import { computed } from "vue";
import { defineStore, storeToRefs } from "pinia";
import { useMCampaignStore } from "@stores/mCampaign";
import { useMCreativesStore } from "@stores/mCreatives";
import { getChatBaseUrl, DEFAULT_API_CHAT_URL } from "@utils/constants/chatUrls";

export const useMCreativeStore = defineStore("mCreative", () => {
  const mCampaignStore = useMCampaignStore();
  const { defaultRegion, defaultLocale, currentChatToken } = storeToRefs(mCampaignStore);

  const mCreativesStore = useMCreativesStore();
  const { selectedMCreative: mCreative } = storeToRefs(mCreativesStore);

  const mediaTypeOptions = [
    { value: "image", label: "Image" },
    { value: "video", label: "Video" },
  ];

  const addCreativeTitle = () => {
    mCreative.value.titles.push("");
  };

  const addCreativeSubtitle = () => {
    mCreative.value.subtitles.push("");
  };

  const removeCreativeTitle = (index) => {
    mCreative.value.titles.splice(index, 1);
  };

  const removeCreativeSubtitle = (index) => {
    mCreative.value.subtitles.splice(index, 1);
  };

  const updateCreativeTitle = (index, value) => {
    mCreative.value.titles[index] = value;
  };

  const updateCreativeSubtitle = (index, value) => {
    mCreative.value.subtitles[index] = value;
  };

  const updateMediaType = (value) => {
    mCreative.value.media_type = value;
    mCreative.value.m_medium_upload_ids = [];
  };

  const updatePlacementFeed = (value) => {
    mCreative.value.placements.feed = value;
    // mCreative.value.m_medium_upload_ids = activeFeedVideos.value;
  };

  const updatePlacementStory = (value) => {
    mCreative.value.placements.story = value;
    // mCreative.value.m_medium_upload_ids = activeStoryVideos.value;
  };

  const updateLocaleId = (value) => {
    const creative = mCreative.value;
    creative.redirection_attributes.locale_id = value;
    if (!parseUrl(currentUrl.value)) resetUrl(creative);
    const url = new URL(currentUrl.value);
    url.searchParams.set("lg", currentLocaleId.value);
    creative.redirection_attributes.url = url.toString();
    replaceDefaultSubtitles(creative);
  };

  const replaceDefaultSubtitles = async (creative) => {
    const defaultsByLanguage = await mCreativesStore.loadDefaultSubtitlesByLanguage();
    const hasDefaultSubtitles = Object.values(defaultsByLanguage).some(
      (defaults) => JSON.stringify(defaults) === JSON.stringify(creative.subtitles),
    );
    if (!hasDefaultSubtitles) return;

    const language = creative.redirection_attributes?.locale_id?.split("-")[0];
    const defaults = defaultsByLanguage[language] ?? defaultsByLanguage.en;
    if (defaults) creative.subtitles = [...defaults];
  };

  const updateRegion = (value) => {
    mCreative.value.redirection_attributes.region = value;
    if (!parseUrl(currentUrl.value)) resetUrl(mCreative.value);
    const url = new URL(currentUrl.value);
    url.searchParams.set("cc", currentRegion.value);
    mCreative.value.redirection_attributes.url = url.toString();
  };

  const updateUrlSuffix = (value) => {
    mCreative.value.redirection_attributes.url = currentUrlPrefix.value + value;
  };

  const setUrl = () => {
    if (parseUrl(currentUrl.value)) return;

    resetUrl(mCreative.value);
    replaceDefaultSubtitles(mCreative.value);
  };

  const chatBaseUrl = computed(() => getChatBaseUrl());

  const resetUrl = (creative) => {
    const url = new URL(chatBaseUrl.value);
    url.searchParams.set("s", currentChatToken.value);
    url.searchParams.set("cc", defaultRegion.value);
    url.searchParams.set("lg", defaultLocale.value);

    creative.redirection_attributes.url = url.toString();
    creative.redirection_attributes.region = defaultRegion.value;
    creative.redirection_attributes.locale_id = defaultLocale.value;
  };

  const currentLocaleId = computed(() => {
    return mCreative.value.redirection_attributes?.locale_id;
  });

  const currentRegion = computed(() => {
    return mCreative.value.redirection_attributes?.region;
  });

  const currentUrl = computed(() => {
    return mCreative.value.redirection_attributes?.url;
  });

  const parseUrl = (value) => {
    try {
      return new URL(value);
    } catch {
      return null;
    }
  };

  const currentUrlPrefix = computed(() => {
    const url = parseUrl(currentUrl.value);
    if (!url) return "";

    const urlPrefix = new URL(url.origin + url.pathname);
    urlPrefix.searchParams.set("s", currentChatToken.value);
    urlPrefix.searchParams.set("cc", currentRegion.value);
    return urlPrefix.toString();
  });

  const currentUrlSuffix = computed(() => {
    if (!currentUrl.value) return "";

    const suffix = currentUrl.value.replace(currentUrlPrefix.value, "");

    // if suffix contains the base URL then rebuild the suffix
    if (suffix.includes(DEFAULT_API_CHAT_URL)) {
      const url = new URL(currentUrl.value);
      url.searchParams.delete("s");
      url.searchParams.delete("cc");
      return `&${url.searchParams.toString()}`;
    }

    return suffix;
  });

  return {
    mCreative,
    mediaTypeOptions,

    addCreativeTitle,
    addCreativeSubtitle,
    removeCreativeTitle,
    removeCreativeSubtitle,
    updateCreativeTitle,
    updateCreativeSubtitle,
    updateMediaType,
    updatePlacementFeed,
    updatePlacementStory,
    updateLocaleId,
    updateRegion,
    updateUrlSuffix,
    setUrl,
    resetUrl,
    replaceDefaultSubtitles,

    currentLocaleId,
    currentRegion,
    currentUrl,
    currentUrlPrefix,
    currentUrlSuffix,
  };
});
